/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import cx from 'classnames';
import { useTheme, Popover, mergeRefs } from '../utils/index.js';
import { Menu } from '../Menu/index.js';
/**
 * Dropdown menu component.
 * Uses the {@link Popover} component, which is a wrapper around [tippy.js](https://atomiks.github.io/tippyjs).
 * @example
 * const menuItems = (close: () => void) => [
 *   <MenuItem key={1} onClick={onClick(1, close)}>
 *     Item #1
 *   </MenuItem>,
 *   <MenuItem key={2} onClick={onClick(2, close)}>
 *     Item #2
 *   </MenuItem>,
 *   <MenuItem key={3} onClick={onClick(3, close)}>
 *     Item #3
 *   </MenuItem>,
 * ];
 * <DropdownMenu menuItems={menuItems}>
 *   <Button>Menu</Button>
 * </DropdownMenu>
 */
export const DropdownMenu = (props) => {
    const { menuItems, children, className, style, role = 'menu', visible, placement = 'bottom-start', onShow, onHide, trigger, id, ...rest } = props;
    const [isVisible, setIsVisible] = React.useState(visible !== null && visible !== void 0 ? visible : false);
    React.useEffect(() => {
        setIsVisible(visible !== null && visible !== void 0 ? visible : false);
    }, [visible]);
    const open = React.useCallback(() => setIsVisible(true), []);
    const close = React.useCallback(() => setIsVisible(false), []);
    const targetRef = React.useRef(null);
    const onShowHandler = React.useCallback((instance) => {
        setIsVisible(true);
        onShow === null || onShow === void 0 ? void 0 : onShow(instance);
    }, [onShow]);
    const onHideHandler = React.useCallback((instance) => {
        var _a;
        setIsVisible(false);
        (_a = targetRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        onHide === null || onHide === void 0 ? void 0 : onHide(instance);
    }, [onHide]);
    useTheme();
    return (React.createElement(Popover, { content: React.createElement(Menu, { className: cx('iui-scroll', className), style: style, role: role, id: id }, React.useMemo(() => menuItems(close), [menuItems, close])), visible: trigger === undefined ? isVisible : undefined, onClickOutside: close, placement: placement, onShow: onShowHandler, onHide: onHideHandler, trigger: visible === undefined ? trigger : undefined, ...rest }, React.cloneElement(children, {
        ref: mergeRefs(targetRef, props.children.ref),
        onClick: (args) => {
            var _a, _b;
            trigger === undefined && (isVisible ? close() : open());
            (_b = (_a = children.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, args);
        },
    })));
};
export default DropdownMenu;
